/**
 * PartnerEarningsTab — admin mirror of fleet-partner's EarningsRecords.tsx.
 *
 * Features:
 * - Stat cards with earnings breakdown
 * - FeeBar inline visualization (partner% vs fee%)
 * - Server-side pagination with search
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, businessWeekBounds,
    formatCurrency, CHART_COLORS, type BusinessWeekBounds,
} from '@/lib/format';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { startOfMonth, subMonths, endOfMonth, addDays, format } from 'date-fns';
import { ListPagination } from '@/components/ListPagination';
import { Search } from 'lucide-react';

interface Props {
    partnerId: number;
}

const PAGE_SIZE = 15;

/**
 * The Ghana business week an ISO week key like '2026-15' names.
 *
 * The RPC builds the key as `to_char(date_trunc('week', payment_date), 'IYYY-IW')`, and ISO week 1
 * is by definition the week containing Jan 4 — so week N contains Jan 4 plus (N-1) weeks. That is a
 * CALENDAR-day Date, which `businessWeekBounds` anchors on its Monday; label, row key and the
 * `?week=` param the row navigates to then all come from the one call, so the week an admin clicks
 * and the week the target page loads are the same week from any browser.
 */
function weekBoundsFromKey(groupKey: string): BusinessWeekBounds {
    const [yearStr, weekStr] = groupKey.split('-');
    const jan4 = new Date(parseInt(yearStr, 10), 0, 4);
    return businessWeekBounds(addDays(jan4, (parseInt(weekStr, 10) - 1) * 7));
}

/** Convert ISO week key like '2026-15' to 'Apr 7 – Apr 13, 2026' */
function weekRangeLabel(groupKey: string): string {
    const { weekStart, weekEnd } = weekBoundsFromKey(groupKey);
    const sameYear = weekStart.getFullYear() === weekEnd.getFullYear();
    if (sameYear) {
        return `${format(weekStart, 'MMM d')} – ${format(weekEnd, 'MMM d, yyyy')}`;
    }
    return `${format(weekStart, 'MMM d, yyyy')} – ${format(weekEnd, 'MMM d, yyyy')}`;
}

interface EarningsRow {
    group_key: string;
    group_label: string;
    vehicle_count: number;
    gross_repayments: number;
    management_fee_amount: number;
    partner_portion: number;
    maintenance_cost: number;
    net_earnings: number;
    repayment_count: number;
    total_groups?: number;
}

/**
 * FeeBar — inline horizontal stacked bar showing partner% vs fee%
 */
function FeeBar({ gross, fee: _fee, partnerPortion }: { gross: number; fee: number; partnerPortion: number }) {
    if (gross <= 0) return <div className="h-2 rounded-full bg-muted w-full" />;
    const partnerPct = (partnerPortion / gross) * 100;
    return (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex">
                <div
                    className="h-full rounded-l-full transition-all duration-300"
                    style={{ width: `${partnerPct}%`, backgroundColor: CHART_COLORS.slate900 }}
                />
                <div
                    className="h-full rounded-r-full transition-all duration-300"
                    style={{ width: `${100 - partnerPct}%`, backgroundColor: CHART_COLORS.slate300 }}
                />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right shrink-0">
                {partnerPct.toFixed(2)}%
            </span>
        </div>
    );
}

export default function PartnerEarningsTab({ partnerId }: Props) {
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    // Business-calendar months; the RPC bounds are built from their parts, never toISOString().
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(timer);
    }, [search]);

    // Server-side paginated earnings data
    const { data: earningsResponse, isLoading } = useQuery({
        queryKey: ['partner-earnings-admin', partnerId, fromInstant, toInstant, currentPage, debouncedSearch],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'week',
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                p_search: debouncedSearch || null,
            });
            if (error) throw error;
            const rows = data as EarningsRow[];
            return {
                rows,
                totalItems: rows.length > 0 ? (rows[0].total_groups ?? rows.length) : 0,
            };
        },
        enabled: !!partnerId,
    });

    // Aggregate totals for stat cards (unpaginated)
    const { data: totals, isLoading: totalsLoading } = useQuery({
        queryKey: ['partner-earnings-totals-admin', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_earnings_explorer', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_vehicle_ids: null,
                p_group_by: 'week',
            });
            if (error) throw error;
            const rows = data as EarningsRow[];
            if (!rows || rows.length === 0) {
                return { gross: 0, fees: 0, earnings: 0, avgRate: 0, count: 0, weekCount: 0 };
            }
            const gross = rows.reduce((s, r) => s + r.gross_repayments, 0);
            const fees = rows.reduce((s, r) => s + r.management_fee_amount, 0);
            const earnings = rows.reduce((s, r) => s + r.partner_portion, 0);
            const count = rows.reduce((s, r) => s + r.repayment_count, 0);
            return {
                gross,
                fees,
                earnings,
                avgRate: gross > 0 ? (fees / gross * 100) : 0,
                count,
                weekCount: rows.length,
            };
        },
        enabled: !!partnerId,
    });

    // Avg weekly earnings from payout summary
    const { data: payoutSummary } = useQuery({
        queryKey: ['partner-payout-summary-avg-admin', partnerId, fromInstant, toInstant],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_partner_payout_summary', {
                p_partner_id: partnerId,
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            return (data as { avg_weekly_partner_portion: number }[])?.[0] ?? null;
        },
        enabled: !!partnerId,
    });

    useEffect(() => { setCurrentPage(1); }, [debouncedSearch, monthRange.from, monthRange.to]);

    const paginatedData = earningsResponse?.rows ?? [];
    const totalItems = earningsResponse?.totalItems ?? 0;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);

    return (
        <div className="space-y-6">
            {/* Date Picker */}
            <div className="flex justify-end">
                <MonthRangePicker monthRange={monthRange} onMonthRangeChange={setMonthRange} />
            </div>

            {/* Stat Cards */}
            {totalsLoading ? (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
                </div>
            ) : (
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    <StatCard title="Gross Repayments" value={<CurrencyDisplay amount={totals?.gross ?? 0} />} description={`Total collected from ${totals?.count ?? 0} driver repayments`} />
                    <StatCard title="Management Fees" value={<CurrencyDisplay amount={totals?.fees ?? 0} />} description={`Primelane's fee at an avg rate of ${(totals?.avgRate ?? 0).toFixed(2)}%`} />
                    <StatCard title="Partner Earnings" value={<CurrencyDisplay amount={totals?.earnings ?? 0} />} description="Partner's share after management fees are deducted" />
                    <StatCard title="Retention Rate" value={`${((totals?.gross ?? 0) > 0 ? ((totals!.earnings / totals!.gross) * 100) : 0).toFixed(2)}%`} description="Percentage of gross repayments partner keeps" />
                    <StatCard title="Avg Weekly Earnings" value={<CurrencyDisplay amount={payoutSummary?.avg_weekly_partner_portion ?? 0} />} description="Average payout per week across all vehicles" />
                </div>
            )}

            {/* Earnings Table */}
            <Card className="border-0">
                <CardContent className="space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input placeholder="Search by week..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                        </div>
                            <ListPagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setCurrentPage} />
                    </div>

                    {isLoading ? (
                        <div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
                    ) : paginatedData.length === 0 ? (
                        <div className="py-16 text-center text-muted-foreground">
                            No earnings data for the selected period.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[220px]">Week</TableHead>
                                        <TableHead className="text-right">Vehicles</TableHead>
                                        <TableHead className="text-right">Gross</TableHead>
                                        <TableHead className="w-[200px]">Split</TableHead>
                                        <TableHead className="text-right">Fee</TableHead>
                                        <TableHead className="text-right">Partner Earnings</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedData.map((row) => {
                                        const feeRate = row.gross_repayments > 0
                                            ? (row.management_fee_amount / row.gross_repayments * 100)
                                            : 0;

                                        return (
                                            <TableRow
                                                key={row.group_key}
                                                className="cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => navigate(`/dashboard/partners/earnings/week?week=${weekBoundsFromKey(row.group_key).weekStartYmd}&partner=${partnerId}`)}
                                            >
                                                <TableCell className="whitespace-nowrap">
                                                    <div className="font-medium">{weekRangeLabel(row.group_key)}</div>
                                                    <div className="text-xs text-muted-foreground">Week {row.group_key.split('-')[1]}</div>
                                                </TableCell>
                                                <TableCell className="text-right tabular-nums">{row.vehicle_count}</TableCell>
                                                <TableCell className="text-right tabular-nums">{formatCurrency(row.gross_repayments)}</TableCell>
                                                <TableCell>
                                                    <FeeBar
                                                        gross={row.gross_repayments}
                                                        fee={row.management_fee_amount}
                                                        partnerPortion={row.partner_portion}
                                                    />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="text-sm tabular-nums text-muted-foreground">
                                                        {formatCurrency(row.management_fee_amount)}
                                                        <span className="ml-1 text-xs">({feeRate.toFixed(2)}%)</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right font-medium tabular-nums">
                                                    {formatCurrency(row.partner_portion)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
